export type RangeCalculatorDisplay = "compact" | "full";

export interface RangeCalculatorMode {
  display: RangeCalculatorDisplay;
  layout: "compact" | "map-and-charts";
}

export function getRangeCalculatorMode(
  display: RangeCalculatorDisplay
): RangeCalculatorMode {
  return {
    display,
    layout: display === "compact" ? "compact" : "map-and-charts",
  };
}

export function clampThrottle(
  throttle: number,
  minThrottle: number,
  maxThrottle: number
): number {
  const min = Number.isFinite(minThrottle) ? minThrottle : 0;
  const max = Number.isFinite(maxThrottle)
    ? Math.max(min, maxThrottle)
    : Math.max(min, 100);

  if (!Number.isFinite(throttle)) return min;
  return Math.min(max, Math.max(min, throttle));
}

export function getMeasuredThrottleBounds(
  dataPoints: Array<{ throttle: number }>
): { minThrottle: number; maxThrottle: number } {
  const measuredThrottles = dataPoints
    .map((point) => point.throttle)
    .filter(Number.isFinite);

  return measuredThrottles.length > 0
    ? {
        minThrottle: Math.min(...measuredThrottles),
        maxThrottle: Math.max(...measuredThrottles),
      }
    : { minThrottle: 0, maxThrottle: 100 };
}

export function clampThrottleToDataPoints(
  throttle: number,
  dataPoints: Array<{ throttle: number }>
): number {
  const { minThrottle, maxThrottle } = getMeasuredThrottleBounds(dataPoints);

  return clampThrottle(throttle, minThrottle, maxThrottle);
}

export function generateThrottleCurveDomain(
  minThrottle: number,
  maxThrottle: number,
  steps: number
): number[] {
  const min = Number.isFinite(minThrottle) ? minThrottle : 0;
  const max = Number.isFinite(maxThrottle)
    ? Math.max(min, maxThrottle)
    : min;
  const curveSteps = Math.max(1, Math.floor(steps));

  return Array.from(
    { length: curveSteps + 1 },
    (_, index) => min + ((max - min) * index) / curveSteps
  );
}
